import { array, boolean, coerce, discriminatedUnion, enum as enumFactory, iso, literal, number, strictObject, string, union } from "../../lazy-zod.js";
import { openMeldRequestContextSchema } from "../daemon/profile-identity.js";
import { daemonServiceLaneSchema } from "../daemon/service-contract.js";
import { slackMessagePermalinkSchema } from "../runtime/message-channel.js";
//#region src/schemas/http/inbox.ts
const INBOX_TITLE_MAX_CHARS = 160;
const INBOX_PREVIEW_MAX_CHARS = 500;
const INBOX_CONTEXT_LABEL_MAX_CHARS = 160;
const INBOX_LIST_DEFAULT_LIMIT = 40;
const INBOX_LIST_MAX_LIMIT = 100;
const INBOX_SPACE_SUMMARY_MAX_SPACES = 500;
const INBOX_SPACE_UNREAD_TARGETS_MAX_ITEMS = 500;
const identifierSchema = string().trim().min(1).max(256);
const timestampMsSchema = number().int().nonnegative();
const isoDateTimeSchema = iso.datetime({ offset: true });
const titleSchema = string().trim().min(1).max(160);
const previewSchema = string().trim().min(1).max(500);
const contextLabelSchema = string().trim().min(1).max(160);
const inboxViewSchema = enumFactory([
	"all",
	"unread",
	"needs_action",
	"needs_review",
	"mentions",
	"updates",
	"urgent_updates",
	"recent_updates"
]);
const inboxPresentationSchema = enumFactory(["attention"]);
const inboxAttentionSchema = enumFactory(["update", "action_required"]);
const inboxImportanceSchema = enumFactory(["normal", "urgent"]);
const inboxRecipientReasonSchema = enumFactory([
	"human_mention",
	"thread_reply",
	"requester",
	"direct_message",
	"space_member",
	"agent_owner",
	"device_owner"
]);
const inboxAgentProfileIdsSchema = array(identifierSchema).max(100);
/** Source-owned relevance, not authorization or notification-channel consent. */
const inboxSourceFactsSchema = strictObject({
	version: literal(1),
	agentProfileIds: inboxAgentProfileIdsSchema.optional(),
	personalAgentProfileIds: inboxAgentProfileIdsSchema.optional(),
	mentionedAgentProfileIds: inboxAgentProfileIdsSchema.optional(),
	firstContactAgentProfileIds: inboxAgentProfileIdsSchema.optional(),
	recipientReasons: array(inboxRecipientReasonSchema).min(1).max(7),
	requestCategory: enumFactory(["action", "review"]).optional()
}).superRefine((facts, context) => {
	const agents = new Set(facts.agentProfileIds ?? []);
	const personalAgents = new Set(facts.personalAgentProfileIds ?? []);
	for (const field of ["personalAgentProfileIds", "mentionedAgentProfileIds"]) if (facts[field]?.some((id) => !agents.has(id))) context.addIssue({
		code: "custom",
		message: "Agent relevance must name an actual Agent in this event.",
		path: [field]
	});
	if (facts.firstContactAgentProfileIds?.some((id) => !personalAgents.has(id))) context.addIssue({
		code: "custom",
		message: "First contact must name the recipient's personal Agent.",
		path: ["firstContactAgentProfileIds"]
	});
	if (personalAgents.size > 0 && !facts.recipientReasons.includes("agent_owner")) context.addIssue({
		code: "custom",
		message: "Personal Agent relevance requires an owner relationship.",
		path: ["recipientReasons"]
	});
});
const inboxResolutionStatusSchema = enumFactory([
	"not_applicable",
	"open",
	"resolved"
]);
const inboxItemKindSchema = enumFactory([
	"space_message",
	"space_mention",
	"agent_reply",
	"agent_interaction",
	"wake_outcome",
	"center_agent_update",
	"document_review",
	"organization_request",
	"system_issue",
	"computer_connection",
	"desktop_update"
]);
const inboxItemWireKindSchema = union([inboxItemKindSchema, literal("space_reference")]);
const inboxSourceKindSchema = enumFactory([
	"space_signal",
	"runtime_coordinator_interaction",
	"remote_agent_native_delivery",
	"remote_agent_native_interaction",
	"organization_document_draft",
	"organization_opportunity",
	"organization_member_request",
	"agent_profile_work_recovery",
	"system_attention_event",
	"computer_connection",
	"desktop_update"
]);
const centerAgentAttentionImpactSchema = enumFactory([
	"response_needed",
	"work_blocked",
	"material_failure_risk"
]);
const centerAgentRequestMemberAttentionBodySchema = strictObject({
	impact: centerAgentAttentionImpactSchema,
	prompt: previewSchema,
	submissionId: identifierSchema,
	title: titleSchema
});
const centerAgentRequestMemberAttentionResponseSchema = strictObject({
	importance: inboxImportanceSchema,
	instruction: string().trim().min(1).max(500),
	organizationId: identifierSchema,
	requestId: identifierSchema
});
const centerAgentInboxSourceTargetSchema = strictObject({
	kind: literal("center_agent"),
	signalId: identifierSchema.optional(),
	spaceId: identifierSchema.optional()
}).superRefine((target, context) => {
	if (target.signalId === void 0 !== (target.spaceId === void 0)) context.addIssue({
		code: "custom",
		message: "Center Agent Inbox targets must include both spaceId and signalId."
	});
});
const slackInboxSourceTargetSchema = strictObject({
	channelId: identifierSchema,
	kind: literal("message_channel"),
	messageId: identifierSchema,
	permalink: slackMessagePermalinkSchema,
	provider: literal("slack"),
	teamId: identifierSchema,
	threadRootMessageId: identifierSchema
});
const computerInboxSourceTargetSchema = strictObject({
	kind: literal("computer_connection"),
	computerId: identifierSchema,
	deviceId: identifierSchema,
	incidentId: identifierSchema,
	lane: daemonServiceLaneSchema,
	state: enumFactory([
		"disconnected",
		"restored",
		"deactivated"
	])
});
const desktopUpdateInboxSourceTargetSchema = strictObject({
	kind: literal("desktop_update"),
	installationId: identifierSchema,
	lane: daemonServiceLaneSchema,
	deviceName: string().trim().min(1).max(120),
	version: string().trim().min(1).max(100),
	platform: enumFactory([
		"darwin",
		"win32",
		"linux"
	]),
	state: enumFactory([
		"available",
		"installed",
		"superseded"
	])
});
const inboxSourceTargetSchema = discriminatedUnion("kind", [
	computerInboxSourceTargetSchema,
	desktopUpdateInboxSourceTargetSchema,
	strictObject({
		kind: literal("organization_join_request"),
		requestId: identifierSchema
	}),
	strictObject({
		kind: literal("agent_profile"),
		profileId: identifierSchema
	}),
	strictObject({
		kind: literal("agent_profile_creation_request"),
		requestId: identifierSchema
	}),
	strictObject({
		kind: literal("space"),
		directMessage: boolean().optional(),
		agentOwnerActivity: boolean().optional(),
		replyToRecipient: boolean().optional(),
		signalId: identifierSchema.optional(),
		spaceId: identifierSchema,
		threadRootSignalId: identifierSchema.optional()
	}),
	strictObject({
		interactionId: identifierSchema,
		kind: literal("agent_interaction"),
		runtimeCoordinatorExecutionId: identifierSchema,
		sourceSignalId: identifierSchema.optional(),
		spaceId: identifierSchema,
		threadRootSignalId: identifierSchema.nullable().optional()
	}),
	strictObject({
		documentId: identifierSchema,
		kind: literal("organization_document")
	}),
	slackInboxSourceTargetSchema,
	centerAgentInboxSourceTargetSchema,
	strictObject({
		kind: literal("settings"),
		section: enumFactory([
			"channels",
			"computers",
			"notifications"
		])
	})
]);
const inboxItemWireSchema = strictObject({
	attention: inboxAttentionSchema,
	contextLabel: contextLabelSchema.nullable(),
	createdAt: isoDateTimeSchema,
	id: identifierSchema,
	importance: inboxImportanceSchema,
	kind: inboxItemWireKindSchema,
	occurredAt: isoDateTimeSchema,
	organizationId: identifierSchema.nullable(),
	preview: previewSchema,
	readAt: isoDateTimeSchema.nullable(),
	recipientUserId: identifierSchema,
	resolutionStatus: inboxResolutionStatusSchema,
	sourceFacts: inboxSourceFactsSchema.optional(),
	sourceTarget: inboxSourceTargetSchema,
	title: titleSchema,
	updatedAt: isoDateTimeSchema
}).superRefine((item, context) => {
	const accountTarget = item.sourceTarget.kind === "computer_connection" || item.sourceTarget.kind === "desktop_update";
	if (item.organizationId === null !== accountTarget || accountTarget && item.kind !== item.sourceTarget.kind) context.addIssue({
		code: "custom",
		message: "Account Inbox scope is reserved for a matching typed device event.",
		path: ["organizationId"]
	});
	if (!(item.attention === "update" ? item.resolutionStatus === "not_applicable" : item.resolutionStatus === "open" || item.resolutionStatus === "resolved")) context.addIssue({
		code: "custom",
		message: "Inbox resolution status must match its attention class.",
		path: ["resolutionStatus"]
	});
});
const inboxItemSchema = inboxItemWireSchema.transform((item) => ({
	...item,
	kind: normalizeInboxItemWireKind(item.kind)
}));
const listInboxItemsQuerySchema = strictObject({
	cursor: string().trim().min(1).max(1024).optional(),
	limit: coerce.number().int().min(1).max(100).optional(),
	presentation: inboxPresentationSchema.optional(),
	notificationPreview: literal("true").optional(),
	view: inboxViewSchema.optional()
});
const inboxSummaryQuerySchema = strictObject({ presentation: inboxPresentationSchema.optional() });
const listInboxItemsResponseSchema = strictObject({
	items: array(inboxItemSchema),
	nextCursor: string().min(1).max(1024).nullable(),
	organizationId: identifierSchema
});
const listInboxItemsWireResponseSchema = strictObject({
	items: array(inboxItemWireSchema),
	nextCursor: string().min(1).max(1024).nullable(),
	organizationId: identifierSchema
});
const inboxSummaryResponseSchema = strictObject({
	priorityCount: number().int().nonnegative().optional(),
	needsActionCount: number().int().nonnegative().optional(),
	needsReviewCount: number().int().nonnegative().optional(),
	unreadPriorityUpdateCount: number().int().nonnegative().optional(),
	organizationId: identifierSchema,
	totalUnreadCount: number().int().nonnegative(),
	unresolvedActionCount: number().int().nonnegative(),
	unreadUrgentUpdateCount: number().int().nonnegative().optional(),
	unreadMentionCount: number().int().nonnegative().optional(),
	ordinaryUnreadCount: number().int().nonnegative().optional()
});
const centerAgentInboxSummaryResponseSchema = strictObject({
	organizationId: identifierSchema,
	unreadCount: number().int().nonnegative()
});
const spaceInboxSummaryItemSchema = strictObject({
	latestUnreadOccurredAtMs: timestampMsSchema,
	spaceId: identifierSchema,
	unreadCount: number().int().positive()
});
const spaceInboxSummaryResponseSchema = strictObject({
	chatUnreadCount: number().int().nonnegative().optional(),
	chatUnresolvedActionCount: number().int().nonnegative().optional(),
	chatUnreadUrgentUpdateCount: number().int().nonnegative().optional(),
	attention: array(strictObject({
		spaceId: identifierSchema,
		unresolvedActionCount: number().int().nonnegative(),
		unreadUrgentUpdateCount: number().int().nonnegative()
	})).optional(),
	ordinarySpaceUnreadCount: number().int().nonnegative().optional(),
	organizationId: identifierSchema,
	spaces: array(spaceInboxSummaryItemSchema).max(500)
});
const spaceInboxUnreadTargetSchema = strictObject({
	occurredAtMs: timestampMsSchema,
	signalId: identifierSchema,
	threadRootSignalId: identifierSchema.nullable()
});
const spaceInboxUnreadTargetsResponseSchema = strictObject({
	hasMoreTargets: boolean(),
	organizationId: identifierSchema,
	spaceId: identifierSchema,
	targets: array(spaceInboxUnreadTargetSchema).max(500),
	totalUnreadCount: number().int().nonnegative()
});
const spaceInboxUnreadTargetsRouteParamsSchema = strictObject({ spaceId: identifierSchema });
const markCenterAgentInboxReadThroughBodySchema = strictObject({
	spaceId: identifierSchema,
	throughOccurredAtMs: timestampMsSchema
});
const markCenterAgentInboxReadThroughResponseSchema = strictObject({
	markedReadCount: number().int().nonnegative(),
	readThroughAt: isoDateTimeSchema
});
const markSpaceInboxReadThroughBodySchema = strictObject({
	spaceId: identifierSchema,
	threadRootSignalId: identifierSchema.optional(),
	throughOccurredAtMs: timestampMsSchema
});
const markSpaceInboxReadThroughResponseSchema = strictObject({
	markedReadCount: number().int().nonnegative(),
	readThroughAt: isoDateTimeSchema
});
const inboxNotificationPreferencesResponseSchema = strictObject({
	updatedAt: isoDateTimeSchema.nullable(),
	urgentEmailEnabled: boolean()
});
const updateInboxNotificationPreferencesBodySchema = strictObject({ urgentEmailEnabled: boolean() });
const inboxMessageNotificationModeSchema = enumFactory(["all_messages", "mentions_and_replies"]);
const inboxConversationNotificationModeSchema = enumFactory([
	"all_messages",
	"mentions_and_replies",
	"muted"
]);
const inboxMessageNotificationPreferencesQuerySchema = strictObject({ spaceId: identifierSchema.optional() });
const inboxMessageNotificationPreferencesResponseSchema = strictObject({
	organizationId: identifierSchema,
	spaceId: identifierSchema.nullable(),
	defaultMode: inboxMessageNotificationModeSchema,
	overrideMode: inboxConversationNotificationModeSchema.nullable(),
	effectiveMode: inboxConversationNotificationModeSchema
});
const updateInboxMessageNotificationPreferencesBodySchema = discriminatedUnion("scope", [strictObject({
	scope: literal("account"),
	mode: inboxMessageNotificationModeSchema
}), strictObject({
	scope: literal("conversation"),
	spaceId: identifierSchema,
	mode: inboxConversationNotificationModeSchema.nullable()
})]);
const getInboxMessageNotificationPreferencesRpcInputSchema = inboxMessageNotificationPreferencesQuerySchema.extend({ context: openMeldRequestContextSchema });
const updateInboxMessageNotificationPreferencesRpcInputSchema = strictObject({
	context: openMeldRequestContextSchema,
	preference: updateInboxMessageNotificationPreferencesBodySchema
});
const inboxItemRouteParamsSchema = strictObject({ itemId: identifierSchema });
const getInboxItemResponseSchema = strictObject({ item: inboxItemSchema });
const getInboxItemWireResponseSchema = strictObject({ item: inboxItemWireSchema });
const dismissInboxItemResponseSchema = strictObject({ dismissedItemId: identifierSchema });
const markInboxItemReadBodySchema = strictObject({ read: boolean() });
const markInboxItemReadResponseSchema = strictObject({ item: inboxItemSchema });
const markInboxItemReadWireResponseSchema = strictObject({ item: inboxItemWireSchema });
const markAllInboxItemsReadResponseSchema = strictObject({ markedReadCount: number().int().nonnegative() });
const markAllInboxItemsReadQuerySchema = strictObject({ presentation: inboxPresentationSchema.optional() });
const inboxSourceCandidateSchema = strictObject({
	attention: inboxAttentionSchema,
	contextLabel: contextLabelSchema.nullable(),
	importance: inboxImportanceSchema,
	kind: inboxItemWireKindSchema,
	occurredAtMs: timestampMsSchema,
	organizationId: identifierSchema,
	preview: previewSchema,
	recipientUserId: identifierSchema,
	sourceKey: identifierSchema,
	sourceKind: inboxSourceKindSchema,
	sourceFacts: inboxSourceFactsSchema.optional(),
	sourceTarget: inboxSourceTargetSchema,
	title: titleSchema
}).transform((candidate) => ({
	...candidate,
	kind: normalizeInboxItemWireKind(candidate.kind)
}));
function normalizeInboxItemWireKind(kind) {
	return kind === "space_reference" ? "space_mention" : kind;
}
const listInboxItemsRpcInputSchema = listInboxItemsQuerySchema.extend({
	context: openMeldRequestContextSchema,
	includeReplyToRecipient: boolean().optional(),
	includeSourceFacts: boolean().optional(),
	includeAccountItems: boolean().optional()
});
const getInboxSummaryRpcInputSchema = strictObject({
	context: openMeldRequestContextSchema,
	includeAccountItems: boolean().optional(),
	presentation: inboxPresentationSchema.optional()
});
const getCenterAgentInboxSummaryRpcInputSchema = strictObject({ context: openMeldRequestContextSchema });
const getSpaceInboxSummaryRpcInputSchema = strictObject({ context: openMeldRequestContextSchema });
const getSpaceInboxUnreadTargetsRpcInputSchema = strictObject({
	context: openMeldRequestContextSchema,
	spaceId: identifierSchema
});
const markCenterAgentInboxReadThroughRpcInputSchema = markCenterAgentInboxReadThroughBodySchema.extend({ context: openMeldRequestContextSchema });
const markSpaceInboxReadThroughRpcInputSchema = markSpaceInboxReadThroughBodySchema.extend({ context: openMeldRequestContextSchema });
const getInboxItemRpcInputSchema = inboxItemRouteParamsSchema.extend({
	context: openMeldRequestContextSchema,
	includeReplyToRecipient: boolean().optional(),
	includeSourceFacts: boolean().optional(),
	includeAccountItems: boolean().optional()
});
const dismissInboxItemRpcInputSchema = inboxItemRouteParamsSchema.extend({
	context: openMeldRequestContextSchema,
	includeAccountItems: boolean().optional()
});
const markInboxItemReadRpcInputSchema = markInboxItemReadBodySchema.extend({
	context: openMeldRequestContextSchema,
	includeReplyToRecipient: boolean().optional(),
	includeSourceFacts: boolean().optional(),
	includeAccountItems: boolean().optional(),
	itemId: identifierSchema
});
const markAllInboxItemsReadRpcInputSchema = strictObject({
	context: openMeldRequestContextSchema,
	presentation: inboxPresentationSchema.optional()
});
const getInboxNotificationPreferencesRpcInputSchema = strictObject({ context: openMeldRequestContextSchema });
const updateInboxNotificationPreferencesRpcInputSchema = updateInboxNotificationPreferencesBodySchema.extend({ context: openMeldRequestContextSchema });
const centerAgentRequestMemberAttentionRpcInputSchema = centerAgentRequestMemberAttentionBodySchema.extend({ context: openMeldRequestContextSchema });
//#endregion
export { INBOX_CONTEXT_LABEL_MAX_CHARS, INBOX_LIST_DEFAULT_LIMIT, INBOX_LIST_MAX_LIMIT, INBOX_PREVIEW_MAX_CHARS, INBOX_SPACE_SUMMARY_MAX_SPACES, INBOX_SPACE_UNREAD_TARGETS_MAX_ITEMS, INBOX_TITLE_MAX_CHARS, centerAgentAttentionImpactSchema, centerAgentInboxSummaryResponseSchema, centerAgentRequestMemberAttentionBodySchema, centerAgentRequestMemberAttentionResponseSchema, centerAgentRequestMemberAttentionRpcInputSchema, computerInboxSourceTargetSchema, desktopUpdateInboxSourceTargetSchema, dismissInboxItemResponseSchema, dismissInboxItemRpcInputSchema, getCenterAgentInboxSummaryRpcInputSchema, getInboxItemResponseSchema, getInboxItemRpcInputSchema, getInboxItemWireResponseSchema, getInboxMessageNotificationPreferencesRpcInputSchema, getInboxNotificationPreferencesRpcInputSchema, getInboxSummaryRpcInputSchema, getSpaceInboxSummaryRpcInputSchema, getSpaceInboxUnreadTargetsRpcInputSchema, inboxAttentionSchema, inboxConversationNotificationModeSchema, inboxImportanceSchema, inboxItemKindSchema, inboxItemRouteParamsSchema, inboxItemSchema, inboxItemWireSchema, inboxMessageNotificationModeSchema, inboxMessageNotificationPreferencesQuerySchema, inboxMessageNotificationPreferencesResponseSchema, inboxNotificationPreferencesResponseSchema, inboxPresentationSchema, inboxRecipientReasonSchema, inboxResolutionStatusSchema, inboxSourceCandidateSchema, inboxSourceFactsSchema, inboxSourceKindSchema, inboxSourceTargetSchema, inboxSummaryQuerySchema, inboxSummaryResponseSchema, inboxViewSchema, listInboxItemsQuerySchema, listInboxItemsResponseSchema, listInboxItemsRpcInputSchema, listInboxItemsWireResponseSchema, markAllInboxItemsReadQuerySchema, markAllInboxItemsReadResponseSchema, markAllInboxItemsReadRpcInputSchema, markCenterAgentInboxReadThroughBodySchema, markCenterAgentInboxReadThroughResponseSchema, markCenterAgentInboxReadThroughRpcInputSchema, markInboxItemReadBodySchema, markInboxItemReadResponseSchema, markInboxItemReadRpcInputSchema, markInboxItemReadWireResponseSchema, markSpaceInboxReadThroughBodySchema, markSpaceInboxReadThroughResponseSchema, markSpaceInboxReadThroughRpcInputSchema, slackInboxSourceTargetSchema, spaceInboxSummaryItemSchema, spaceInboxSummaryResponseSchema, spaceInboxUnreadTargetSchema, spaceInboxUnreadTargetsResponseSchema, spaceInboxUnreadTargetsRouteParamsSchema, updateInboxMessageNotificationPreferencesBodySchema, updateInboxMessageNotificationPreferencesRpcInputSchema, updateInboxNotificationPreferencesBodySchema, updateInboxNotificationPreferencesRpcInputSchema };

//# sourceMappingURL=inbox.js.map